// SolutionShowcase.js - "/workflow": an immersive dark showroom. Glass
// chapters on the brand-ink navy with brand-gradient aurora light, a sticky
// glass chip nav with scrollspy, and cursor-spotlight feature cards. Same
// theme palette, presented dark. Used only by WorkFlow.js.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { FeatureCard, ScreenshotFrame } from "./ui";

// Scroll offsets must match .ssw-chapter { scroll-margin-top } in the CSS:
// header (76/68) + glass nav bar + breathing room.
const OFFSET_DESKTOP = 156;
const OFFSET_MOBILE = 136;
const MQ_DESKTOP = "(min-width: 992px)";

const reducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Faq({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="sol-faq">
      {items.map(({ q, a }, i) => {
        const isOpen = open === i;
        return (
          <div className={`sol-faq__item ${isOpen ? "open" : ""}`} key={q}>
            <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : i)}>
              {q}
              <ChevronDown size={18} aria-hidden="true" />
            </button>
            {isOpen && <div className="sol-faq__answer">{a}</div>}
          </div>
        );
      })}
    </div>
  );
}

function useScrollSpy(ids) {
  // The hash is written with history.replaceState, which bypasses the router,
  // so location is read-only here; active state lives in this hook.
  const location = useLocation();
  const [activeId, setActiveId] = useState(() => {
    const h = window.location.hash.slice(1);
    return ids.includes(h) ? h : ids[0];
  });

  const activeRef = useRef(activeId);
  activeRef.current = activeId;
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const pinRef = useRef(null); // section held at the scroll offset while layout settles
  const observerRef = useRef(null);
  const suppressHashRef = useRef(false);
  const timerRef = useRef(0);

  const setActive = useCallback((id, { updateHash = true } = {}) => {
    if (activeRef.current !== id) {
      activeRef.current = id;
      setActiveId(id);
    }
    if (updateHash && window.location.hash !== `#${id}`) {
      window.history.replaceState(null, "", `#${id}`);
    }
  }, []);

  const endJump = useCallback(() => {
    suppressHashRef.current = false;
    pinRef.current = null;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // While a jump is settling, re-pin the target whenever the layout above it
  // changes (late fonts, images); released once layout is quiet, capped at 3s.
  const beginJump = useCallback(
    id => {
      suppressHashRef.current = true;
      pinRef.current = id;
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(endJump, 3000);
      if (!observerRef.current && typeof ResizeObserver !== "undefined") {
        observerRef.current = new ResizeObserver(() => {
          const pinned = pinRef.current;
          if (!pinned) return;
          const el = document.getElementById(pinned);
          if (!el) return;
          const offset = window.matchMedia(MQ_DESKTOP).matches ? OFFSET_DESKTOP : OFFSET_MOBILE;
          const delta = el.getBoundingClientRect().top - offset;
          if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: "instant" });
          window.clearTimeout(timerRef.current);
          timerRef.current = window.setTimeout(endJump, 600);
        });
        observerRef.current.observe(document.body);
      }
    },
    [endJump]
  );

  const jumpTo = useCallback(
    id => {
      const el = document.getElementById(id);
      if (!el) return;
      beginJump(id);
      setActive(id);
      el.scrollIntoView({ behavior: reducedMotion() ? "instant" : "smooth", block: "start" });
      document.getElementById(`${id}-title`)?.focus({ preventScroll: true });
    },
    [setActive, beginJump]
  );

  // Release pin + observer if we unmount mid-jump (StrictMode-safe).
  useEffect(() => () => endJump(), [endJump]);

  // Deep links (/workflow#defect-management) and later router hash changes.
  const didMountRef = useRef(false);
  const hash = location.hash;
  useEffect(() => {
    const first = !didMountRef.current;
    didMountRef.current = true;
    const h = hash.slice(1);
    if (!h || !idsRef.current.includes(h)) return;
    if (first) {
      const el = document.getElementById(h);
      if (!el) return;
      beginJump(h);
      setActive(h);
      el.scrollIntoView({ behavior: "instant", block: "start" });
      document.getElementById(`${h}-title`)?.focus({ preventScroll: true });
    } else {
      jumpTo(h);
    }
    // setActive/jumpTo are stable; the id list comes from idsRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  // Scrollspy: one rAF-coalesced measurement per frame; the active chapter is
  // the last one whose top has crossed the line under header + nav.
  useEffect(() => {
    let frame = 0;
    let initial = true; // don't stamp a hash on a page opened without one
    const measure = () => {
      frame = 0;
      const list = idsRef.current;
      const offset = window.matchMedia(MQ_DESKTOP).matches ? OFFSET_DESKTOP : OFFSET_MOBILE;
      const line = window.scrollY + offset + 1;
      let current = list[0];
      for (const id of list) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= line) current = id;
      }
      const doc = document.documentElement;
      if (window.innerHeight + Math.ceil(window.scrollY) >= doc.scrollHeight - 2) {
        current = list[list.length - 1];
      }
      setActive(current, { updateHash: !initial && !suppressHashRef.current });
      initial = false;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.clearTimeout(timerRef.current);
    };
  }, [setActive]);

  return { activeId, jumpTo };
}

function ShowcaseNav({ tabs, activeId, jumpTo, progress, label }) {
  const listRef = useRef(null);

  // Keep the active chip visible in the horizontal bar: whenever the active
  // chapter changes (a chip click or the scrollspy), the bar glides so the
  // chip sits centred. Runs at every width — on desktop the full chip row
  // overflows too (chapters 07/08 sit past the right edge). scrollTo moves
  // only the bar's own overflow, never the page.
  useEffect(() => {
    const bar = listRef.current;
    const chip = bar?.querySelector('[aria-current="true"]');
    if (bar && chip) {
      bar.scrollTo({
        left: chip.offsetLeft - (bar.clientWidth - chip.clientWidth) / 2,
        behavior: reducedMotion() ? "instant" : "smooth",
      });
    }
  }, [activeId]);

  return (
    <div className="ssw-nav" role="navigation" aria-label={`${label} sections`} style={{ "--ssw-progress": `${progress}` }}>
      <ul className="ssw-nav__list" ref={listRef}>
        {tabs.map((t, i) => (
          <li key={t.id}>
            <a
              href={`#${t.id}`}
              className="ssw-chip"
              aria-current={t.id === activeId ? "true" : undefined}
              onClick={e => {
                e.preventDefault();
                jumpTo(t.id);
              }}
            >
              <span className="ssw-chip__num" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="ssw-chip__label">{t.label}</span>
            </a>
          </li>
        ))}
      </ul>
      <span className="ssw-nav__bar" aria-hidden="true">
        <span className="ssw-nav__bar-fill" />
      </span>
    </div>
  );
}

// Cursor spotlight for feature-card grids: --ssw-mx/--ssw-my feed the radial
// highlight in the CSS.
const moveSpotlight = e => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  el.style.setProperty("--ssw-mx", `${e.clientX - rect.left}px`);
  el.style.setProperty("--ssw-my", `${e.clientY - rect.top}px`);
};

// Props never change (the tabs array is module-level data), so memo keeps the
// eleven chapters from re-rendering on scrollspy updates.
const Chapter = memo(function Chapter({ tab, index }) {
  const Icon = tab.icon;
  return (
    <section className="ssw-chapter" id={tab.id} aria-labelledby={`${tab.id}-title`}>
      <span className="ssw-chapter__ghost" aria-hidden="true">
        {String(index + 1).padStart(2, "0")}
      </span>
      <p className="ssw-chapter__eyebrow">{tab.label}</p>
      <h2 className="ssw-chapter__title" id={`${tab.id}-title`} tabIndex={-1}>
        {Icon && <Icon size={26} aria-hidden="true" />}
        {tab.title}
      </h2>

      <div className={tab.image ? "ft-split ssw-chapter__intro" : "ssw-chapter__intro ssw-text-only"}>
        <div>
          {tab.paragraphs.map(p => (
            <p className="ssw-text" key={typeof p === "string" ? p : p.key}>
              {p}
            </p>
          ))}
          {tab.bullets && (
            <ul className="ft-checklist ssw-checklist mt-3">
              {tab.bullets.map(b => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </div>
        {tab.image &&
          (tab.screenshot ? (
            <ScreenshotFrame src={tab.image} alt={tab.imageAlt} height={tab.imageHeight} />
          ) : (
            <img className="sol-illustration" src={tab.image} alt={tab.imageAlt} loading="lazy" />
          ))}
      </div>

      {tab.sections && (
        <div className="sol-sections">
          {tab.sections.map(s => (
            <div className="sol-subsection" key={s.title}>
              <h3>{s.title}</h3>
              {s.text.map(t => (
                <p key={t}>{t}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      {tab.cards && (
        <div className={`ft-grid ${tab.cards.length % 3 === 0 ? "ft-grid--3" : "ft-grid--2"} ssw-grid mt-4`} onMouseMove={moveSpotlight}>
          {tab.cards.map(c => (
            <FeatureCard key={c.title} {...c} />
          ))}
        </div>
      )}

      {tab.faqs && (
        <>
          <h3 className="sol-faq__title">Frequently asked questions</h3>
          <Faq items={tab.faqs} />
        </>
      )}
    </section>
  );
});

export default function SolutionShowcase({ tabs, label = "Solutions" }) {
  const ids = useMemo(() => tabs.map(t => t.id), [tabs]);
  const { activeId, jumpTo } = useScrollSpy(ids);
  const activeIndex = Math.max(0, tabs.findIndex(t => t.id === activeId));
  const progress = tabs.length > 1 ? activeIndex / (tabs.length - 1) : 1;

  return (
    <section className="ft-section ssw" aria-label={label}>
      <div className="ft-container ssw-container">
        <ShowcaseNav tabs={tabs} activeId={activeId} jumpTo={jumpTo} progress={progress} label={label} />
        <div className="ssw-chapters">
          {tabs.map((t, i) => (
            <Chapter key={t.id} tab={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
